/**
 * State management hook for the Portfolio Insights page.
 *
 * Owns the page lifecycle (`loading | shown | empty | retrieveError |
 * generating | genError`), the current report, the live activity list, the
 * last error message, and the AbortController for the one in-flight request.
 *
 * - `loadLatest(accountId)` fetches the stored report (Req 8.2). Responses for a
 *   superseded request (account changed mid-flight) are ignored.
 * - `generate(accountId)` runs at most one generation at a time (Req 2.2). A
 *   failed generation keeps the previously shown report (Req 8.7, 10.4).
 * - Unmounting, changing account, or starting another request aborts the
 *   in-flight request and stops showing progress (Req 7.4). Aborts are not errors.
 *
 * The page is advisory only: nothing here submits or routes a trade.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useAuth } from './useAuth';
import {
  fetchLatestInsights,
  generateInsights,
  InsightsError,
  type InsightsErrorKind,
} from '@/services/insightsApi';
import type { Activity } from '@/types/chat';
import type { InsightsReport } from '@/types/insights';

// --- Types ---

export type InsightsStatus =
  | 'loading'
  | 'shown'
  | 'empty'
  | 'retrieveError'
  | 'generating'
  | 'genError';

export interface InsightsState {
  status: InsightsStatus;
  /** Most recent report for the account. Retained across a failed generation. */
  report: InsightsReport | null;
  /** Live provenance/progress activities for the generation in progress. */
  activities: Activity[];
  /** User-safe message for `retrieveError` / `genError`, otherwise null. */
  error: string | null;
  /** Category of `error`, so the page can react to an expired session. */
  errorKind: InsightsErrorKind | null;
  /**
   * `generated_at` of the report produced by the latest successful generation,
   * or null. The page uses it to show a success indication (Req 2.4).
   */
  lastGeneratedAt: string | null;
}

export interface UseInsightsReturn extends InsightsState {
  /** Load the stored report for an account. `null` cancels work and clears the page. */
  loadLatest: (accountId: string | null) => Promise<void>;
  /** Start one generation. Ignored while a generation is already in flight. */
  generate: (accountId: string) => Promise<void>;
  /** Re-run `loadLatest` for the most recently requested account. */
  retry: () => Promise<void>;
}

type InsightsAction =
  | { type: 'LOAD_STARTED' }
  | { type: 'LOAD_SUCCEEDED'; report: InsightsReport }
  | { type: 'LOAD_EMPTY' }
  | { type: 'LOAD_FAILED'; error: string; kind: InsightsErrorKind | null }
  | { type: 'GENERATE_STARTED' }
  | { type: 'ACTIVITY'; activity: Activity }
  | { type: 'GENERATE_SUCCEEDED'; report: InsightsReport }
  | { type: 'GENERATE_FAILED'; error: string; kind: InsightsErrorKind | null };

// --- Constants ---

const AUTH_MESSAGE = 'Your session has expired. Please sign in again.';
const LOAD_FAILED_MESSAGE = 'Unable to load the latest insights. Please try again.';
const GENERATE_FAILED_MESSAGE = 'Insights generation did not complete. Please try again.';

export const initialInsightsState: InsightsState = {
  status: 'loading',
  report: null,
  activities: [],
  error: null,
  errorKind: null,
  lastGeneratedAt: null,
};

// --- Reducer ---

/** A `started` activity appends; a terminal one updates in place, or appends if unmatched. */
function applyActivity(existing: Activity[], activity: Activity): Activity[] {
  if (activity.status === 'started') return [...existing, activity];
  const idx = existing.findIndex((a) => a.id === activity.id);
  if (idx === -1) return [...existing, activity];
  return existing.map((a, i) =>
    i === idx
      ? {
          ...a,
          status: activity.status,
          detail: activity.detail ? { ...a.detail, ...activity.detail } : a.detail,
        }
      : a
  );
}

export function insightsReducer(state: InsightsState, action: InsightsAction): InsightsState {
  switch (action.type) {
    case 'LOAD_STARTED':
      // A different account's report must never be shown, so the report is cleared.
      return { ...initialInsightsState };
    case 'LOAD_SUCCEEDED':
      return { ...initialInsightsState, status: 'shown', report: action.report };
    case 'LOAD_EMPTY':
      return { ...initialInsightsState, status: 'empty' };
    case 'LOAD_FAILED':
      return {
        ...initialInsightsState,
        status: 'retrieveError',
        error: action.error,
        errorKind: action.kind,
      };
    case 'GENERATE_STARTED':
      // The current report is retained so a failure leaves it available.
      return {
        ...state,
        status: 'generating',
        activities: [],
        error: null,
        errorKind: null,
        lastGeneratedAt: null,
      };
    case 'ACTIVITY':
      if (state.status !== 'generating') return state;
      return { ...state, activities: applyActivity(state.activities, action.activity) };
    case 'GENERATE_SUCCEEDED':
      return {
        ...initialInsightsState,
        status: 'shown',
        report: action.report,
        lastGeneratedAt: action.report.generated_at,
      };
    case 'GENERATE_FAILED':
      return {
        ...state,
        status: 'genError',
        activities: [],
        error: action.error,
        errorKind: action.kind,
        lastGeneratedAt: null,
      };
    default:
      return state;
  }
}

// --- Hook ---

export function useInsights(): UseInsightsReturn {
  const [state, dispatch] = useReducer(insightsReducer, initialInsightsState);
  const { getToken } = useAuth();

  // Keep callbacks stable even if the auth context hands back a new getToken.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  /** Controller of the single in-flight load or generation. */
  const controllerRef = useRef<AbortController | null>(null);
  const generatingRef = useRef(false);
  const accountIdRef = useRef<string | null>(null);

  /** Abort whatever is in flight. Its late responses are then ignored. */
  const abortInFlight = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    generatingRef.current = false;
  }, []);

  // Navigating away stops the request and progress display (Req 7.4).
  useEffect(() => abortInFlight, [abortInFlight]);

  const loadLatest = useCallback(
    async (accountId: string | null) => {
      abortInFlight();
      accountIdRef.current = accountId;

      if (!accountId) {
        dispatch({ type: 'LOAD_EMPTY' });
        return;
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      dispatch({ type: 'LOAD_STARTED' });

      try {
        const token = await getTokenRef.current();
        if (controller.signal.aborted) return;
        if (!token) {
          dispatch({ type: 'LOAD_FAILED', error: AUTH_MESSAGE, kind: 'auth' });
          return;
        }

        const response = await fetchLatestInsights(accountId, token, controller.signal);
        if (controller.signal.aborted) return;
        if (response.report) {
          dispatch({ type: 'LOAD_SUCCEEDED', report: response.report });
        } else {
          dispatch({ type: 'LOAD_EMPTY' });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        dispatch({
          type: 'LOAD_FAILED',
          error: error instanceof InsightsError ? error.message : LOAD_FAILED_MESSAGE,
          kind: error instanceof InsightsError ? error.kind : null,
        });
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    [abortInFlight]
  );

  const generate = useCallback(
    async (accountId: string) => {
      if (generatingRef.current) return;

      // Supersede a pending load (a no-op when nothing is in flight).
      abortInFlight();
      generatingRef.current = true;

      const controller = new AbortController();
      controllerRef.current = controller;
      dispatch({ type: 'GENERATE_STARTED' });

      let received: InsightsReport | null = null;

      try {
        const token = await getTokenRef.current();
        if (controller.signal.aborted) return;
        if (!token) {
          dispatch({ type: 'GENERATE_FAILED', error: AUTH_MESSAGE, kind: 'auth' });
          return;
        }

        await generateInsights(accountId, token, {
          signal: controller.signal,
          onActivity: (activity) => {
            if (!controller.signal.aborted) dispatch({ type: 'ACTIVITY', activity });
          },
          onReport: (report) => {
            received = report;
          },
          onComplete: () => {
            if (controller.signal.aborted) return;
            if (received) {
              dispatch({ type: 'GENERATE_SUCCEEDED', report: received });
            } else {
              dispatch({ type: 'GENERATE_FAILED', error: GENERATE_FAILED_MESSAGE, kind: 'failed' });
            }
          },
          onError: (error) => {
            if (controller.signal.aborted) return;
            dispatch({ type: 'GENERATE_FAILED', error: error.message, kind: error.kind });
          },
        });
      } catch {
        // generateInsights reports failures through onError; this covers anything unexpected.
        if (!controller.signal.aborted) {
          dispatch({ type: 'GENERATE_FAILED', error: GENERATE_FAILED_MESSAGE, kind: 'failed' });
        }
      } finally {
        // Only release the guard if a newer request has not taken over.
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          generatingRef.current = false;
        }
      }
    },
    [abortInFlight]
  );

  const retry = useCallback(async () => {
    if (!accountIdRef.current) return;
    await loadLatest(accountIdRef.current);
  }, [loadLatest]);

  return { ...state, loadLatest, generate, retry };
}
